import EjectIcon from '@mui/icons-material/Eject';
import {
  Grid,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { TextAsLink, nameDiv, pointerCursor, rotateIcon } from '../../styles';

export default function ListTable(props) {
  const { rows, navigateToURL, orderHandler } = props;
  const { t } = useTranslation();

  const columns = [
    { id: 'ID', label: t('ID') },
    { id: 'Status', label: t('Next_Step') },
    { id: 'Title', label: t('Title') },
    { id: 'CreatedDateSort', label: t('Creation_Date') },
    { id: 'UserName', label: t('Site_Creator') },
    { id: 'Description', label: t('Description') },
    { id: 'ProductFamilyID', label: t('Product_Family') },
    { id: 'SitesImpacted', label: t('Sites_Impacted') },
    { id: 'actionToDo', label: t('action_to_do') },
  ];

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [columnOrderData, setColumnOrderData] = React.useState({
    columnId: 'ID',
    order: 'desc',
  });

  //to set the page index as 0 whenever rows(list) is changing
  React.useEffect(() => {
    setPage(0);
  }, [rows]);

  //To handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  //To handle the change of rows per page
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //To handle the Sorting
  const handleOrderChange = (columnId, order) => {
    setColumnOrderData({
      columnId: columnId,
      order: order,
    });
    orderHandler(columnId, order);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#1F305E',
      color: theme.palette.common.white,
      fontWeight: 'bold',
      fontSize: '17px',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: '17px',
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
  }));

  return rows.length > 0 ? (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <StyledTableCell key={column.id} align="center">
                  <Grid style={nameDiv}>
                    <Grid>{column.label}</Grid>
                    <Grid>
                      {column.id !== 'actionToDo' &&
                        (column.id === columnOrderData.columnId ? (
                          columnOrderData.order === 'desc' ? (
                            <EjectIcon
                              style={pointerCursor}
                              onClick={() =>
                                handleOrderChange(column.id, 'asc')
                              }
                            />
                          ) : (
                            <EjectIcon
                              style={rotateIcon}
                              onClick={() =>
                                handleOrderChange(column.id, 'desc')
                              }
                            />
                          )
                        ) : (
                          <Grid
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <EjectIcon
                              style={pointerCursor}
                              onClick={() =>
                                handleOrderChange(column.id, 'desc')
                              }
                              fontSize="30px"
                            />
                            <EjectIcon
                              style={rotateIcon}
                              onClick={() =>
                                handleOrderChange(column.id, 'asc')
                              }
                              fontSize="30px"
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Grid>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                  >
                    <StyledTableCell align="center">
                      <Typography width={50} sx={{ cursor: 'default' }}>
                        {row.PECRID}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography width={120} sx={{ cursor: 'default' }}>
                        {row.Status}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Tooltip
                        title={row.Title}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '14px',
                            },
                          },
                        }}
                      >
                        <Typography
                          noWrap
                          width={120}
                          sx={{ cursor: 'default' }}
                        >
                          {row.Title}
                        </Typography>
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.CreationDate}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography width={120} sx={{ cursor: 'default' }}>
                        {row.UserName}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Tooltip
                        title={row.Description}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: '14px',
                            },
                          },
                        }}
                      >
                        <Typography
                          noWrap
                          width={300}
                          sx={{ cursor: 'default' }}
                        >
                          {row.Description}
                        </Typography>
                      </Tooltip>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography width={180} sx={{ cursor: 'default' }}>
                        {row.ProductFamily}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {row.SitesImpacted &&
                        row?.SitesImpacted?.split(',').map((site, index) => {
                          return (
                            <Typography
                              key={index}
                              width={100}
                              sx={{ cursor: 'default' }}
                            >
                              {site}
                            </Typography>
                          );
                        })}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Grid
                        style={TextAsLink}
                        onClick={() => navigateToURL(row.URL)}
                      >
                        <Typography width={120}>{row.Actiontodo}</Typography>
                      </Grid>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={`${t('Rows_per_page')}:`}
      />
    </Paper>
  ) : null;
}
